import { Fragment, ReactElement, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Input,
  LinkButton,
  Modal,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  Spinner,
  Typography,
} from '@weareopenr/component-library';
import styles from './RoleModal.module.scss';
import { IFormValues, IRoleModalProps } from './types';
import { useCreateRole } from 'src/hooks/useCreateRole/useCreateRole';
import { useUpdateRole } from 'src/hooks/useUpdateRole/useUpdateRole';
import { usePermissionCatalogue } from 'src/hooks/usePermissionCatalogue/usePermissionCatalogue';
import { IRole } from 'src/types';
import { allGroupActions, toggleGroup, togglePermission, IPermissionGroup } from './permissions';
import { filterSections } from './search';
import PermissionGroupCard from './PermissionGroupCard';
import RoleSummary from './RoleSummary';

const RoleModal = ({ closeModal, roles, role }: Omit<IRoleModalProps, 'open'>) => {
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(role?.permissions ?? []);
  const [expandedGroups, setExpandedGroups] = useState<Set<string> | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const onMutationSuccess = (): void => {
    closeModal();
  };
  const { mutate: createRoleMutate, isLoading: isCreateRoleLoading } =
    useCreateRole(onMutationSuccess);
  const { mutate: updateRoleMutate, isLoading: isUpdateRoleLoading } =
    useUpdateRole(onMutationSuccess);

  const {
    catalogue,
    sections: permissionSections,
    isLoading: isPermissionsLoading,
    isError: isPermissionsError,
  } = usePermissionCatalogue();
  const allPermissionSlugs = useMemo(
    () => (catalogue?.permissions ?? []).map((permission) => permission.slug),
    [catalogue],
  );
  const groupsByCollection = useMemo(
    () =>
      new Map(
        permissionSections
          .flatMap((section) => section.groups)
          .map((group) => [group.collection, group]),
      ),
    [permissionSections],
  );

  const isSearching = searchTerm.trim().length > 0;
  const visibleSections = useMemo(
    () => filterSections(permissionSections, searchTerm),
    [permissionSections, searchTerm],
  );
  const initialExpanded = useMemo(
    () =>
      new Set(
        permissionSections
          .flatMap((section) => section.groups)
          .filter((group) =>
            allGroupActions(group).some((groupAction) =>
              (role?.permissions ?? []).includes(groupAction.slug),
            ),
          )
          .map((group) => group.collection),
      ),
    [permissionSections, role],
  );
  const effectiveExpanded = expandedGroups ?? initialExpanded;

  const isGroupExpanded = (collection: string): boolean =>
    isSearching || effectiveExpanded.has(collection);

  const handleToggleExpand = (collection: string) => {
    if (isSearching) return;
    const next = new Set(effectiveExpanded);
    if (next.has(collection)) {
      next.delete(collection);
    } else {
      next.add(collection);
    }
    setExpandedGroups(next);
  };

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<IFormValues>({
    defaultValues: {
      name: role?.name ?? '',
      description: role?.description ?? '',
    },
    mode: 'onChange',
  });

  const handleToggleAction = (group: IPermissionGroup, slug: string, checked: boolean) => {
    setSelectedPermissions((current) => togglePermission(current, group, slug, checked));
  };

  const handleToggleGroup = (group: IPermissionGroup, checked: boolean) => {
    setSelectedPermissions((current) => toggleGroup(current, group, checked));
  };

  const toggleAllPermissions = (checked: boolean) => {
    setSelectedPermissions((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...allPermissionSlugs]));
      }
      const known = new Set(allPermissionSlugs);
      return current.filter((slug) => !known.has(slug));
    });
  };

  const selectedSet = useMemo(() => new Set(selectedPermissions), [selectedPermissions]);
  const allPermissionsSelected =
    allPermissionSlugs.length > 0 && allPermissionSlugs.every((slug) => selectedSet.has(slug));

  const onSubmit = (data: IFormValues) => {
    const name = data.name.trim();
    const description = data.description.trim();

    if (role) {
      updateRoleMutate({
        slug: role.slug,
        name,
        description: description || null,
        permissions: selectedPermissions,
      });
      return;
    }

    createRoleMutate({
      name,
      ...(description && { description }),
      permissions: selectedPermissions,
    });
  };

  const renderPermissionGroup = (filteredGroup: IPermissionGroup): ReactElement => {
    const group = groupsByCollection.get(filteredGroup.collection) ?? filteredGroup;
    return (
      <PermissionGroupCard
        key={group.collection}
        group={group}
        visibleGroup={filteredGroup}
        selectedPermissions={selectedPermissions}
        isExpanded={isGroupExpanded(group.collection)}
        onToggleExpand={() => handleToggleExpand(group.collection)}
        onToggleGroup={(checked) => handleToggleGroup(group, checked)}
        onToggleAction={(slug, checked) => handleToggleAction(group, slug, checked)}
      />
    );
  };

  const renderPermissions = (): ReactElement => {
    if (isPermissionsLoading) {
      return (
        <div className={styles.spinner}>
          <Spinner size="large" />
        </div>
      );
    }

    if (isPermissionsError) {
      return (
        <Typography size="small" className={styles['permissions-error']}>
          Could not load permissions. Close the modal and try again.
        </Typography>
      );
    }

    return (
      <div className={styles['permissions-container']}>
        {visibleSections.map((section) => (
          <Fragment key={section.key}>
            <Typography size="large" weight="semi-bold" className={styles['application-heading']}>
              {section.label}
            </Typography>
            {section.groups.map(renderPermissionGroup)}
          </Fragment>
        ))}
      </div>
    );
  };

  return (
    <form className={styles['form']} onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['heading-text']}>
        {role ? 'Edit role' : 'Create role'}
      </Typography>
      <div className={styles['columns']}>
        <div className={styles['details-column']}>
          <div className={styles['input-field']}>
            <Input
              type="text"
              label="Name"
              register={register('name', {
                required: 'Please enter a name',
                validate: {
                  nameIsUnique: (value: string) =>
                    roles?.some(
                      (existing: IRole) =>
                        existing.id !== role?.id &&
                        existing.name.toLowerCase() === value.trim().toLowerCase(),
                    )
                      ? 'A Role with this name already exists'
                      : true,
                },
              })}
              error={errors?.name?.message}
            />
          </div>
          <div className={styles['input-field']}>
            <Input
              type="text"
              label="Description (optional)"
              rows={3}
              register={register('description')}
              error={errors?.description?.message}
            />
          </div>
          <RoleSummary sections={permissionSections} selectedPermissions={selectedPermissions} />
        </div>
        <div className={styles['permissions-column']}>
          <div className={styles['permissions-label']}>
            <Typography size="large" weight="semi-bold">
              Permissions
            </Typography>
            <SearchField
              className={styles['permissions-search']}
              placeholder="Search permissions"
              isDisabled={isPermissionsLoading}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {allPermissionSlugs.length > 0 && (
              <LinkButton
                type="button"
                onClick={() => toggleAllPermissions(!allPermissionsSelected)}
              >
                {allPermissionsSelected ? 'Clear all' : 'Select all'}
              </LinkButton>
            )}
          </div>
          {renderPermissions()}
        </div>
      </div>
      <div className={styles['footer']}>
        <SecondaryButton type="button" onClick={closeModal}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          data-cy="create-role-button"
          loading={isCreateRoleLoading || isUpdateRoleLoading}
          type="submit"
        >
          {role ? 'Save' : 'Create'}
        </PrimaryButton>
      </div>
    </form>
  );
};

const WrappedModal = ({ closeModal, open, roles, role }: IRoleModalProps): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={open}>
      <RoleModal closeModal={closeModal} roles={roles} role={role} />
    </Modal>
  );
};

export default WrappedModal;
